// src/pages/users/drivers/Drivers.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Form,
  InputGroup,
  FormControl,
  Pagination,
  Image,
  Tabs,
  Tab,
} from "react-bootstrap";
import api from "../../../services/api";
import { Colors } from "../../../constants/Colors";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const MySwal = withReactContent(Swal);
const PER_PAGE = 10;

/* ---------------- Design tokens ---------------- */
const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

const FALLBACK_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "") ||
  "http://localhost:8000";

/* ---------------- Status metadata ---------------- */
const STATUS_META = {
  available: {
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1f`,
    label: "Available",
  },
  on_trip: {
    color: Colors.WARNING,
    bg: `${Colors.WARNING}1f`,
    label: "On Trip",
  },
  offline: { color: Colors.GRAY, bg: `${Colors.GRAY}26`, label: "Offline" },
  suspended: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Suspended" },
  terminated: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Terminated" },
};

const STATUS_OPTIONS = [
  { value: "All", label: "All Status" },
  { value: "available", label: "Available" },
  { value: "on_trip", label: "On Trip" },
  { value: "offline", label: "Offline" },
  { value: "suspended", label: "Suspended" },
  { value: "terminated", label: "Terminated" },
];

const VERIFICATION_OPTIONS = [
  { value: "", label: "All Verification" },
  { value: "1", label: "Verified Only" },
  { value: "0", label: "Unverified Only" },
];

/* ---------------- Stat card definitions ---------------- */
const STAT_CARDS = [
  { k: "Total", field: "total", i: "bi-people", c: Colors.PRIMARY },
  {
    k: "Available",
    field: "available",
    i: "bi-person-check",
    c: Colors.SUCCESS,
  },
  { k: "On Trip", field: "on_trip", i: "bi-truck", c: Colors.WARNING },
  { k: "Offline", field: "offline", i: "bi-person-dash", c: Colors.GRAY },
  { k: "Suspended", field: "suspended", i: "bi-person-x", c: Colors.RED },
  { k: "Verified", field: "verified", i: "bi-patch-check", c: Colors.INFO },
];

/* ---------------- Empty detail form ---------------- */
const EMPTY_FORM = {
  driver_id: null,
  full_name: "",
  first_name: "",
  last_name: "",
  middle_name: "",
  suffix: "",
  email: "",
  phone: "",
  license_number: "",
  license_expiry_date: "",
  license_type: "",
  address: "",
  city: "",
  province: "",
  postal_code: "",
  date_of_birth: "",
  gender: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
  hire_date: "",
  base_salary: "",
  employment_type: "",
  status: "",
  status_label: "",
  status_color: "",
  is_verified: false,
  verified_at: "",
  profile_image_url: null,
  license_image_front_url: null,
  license_image_back_url: null,
  average_rating: 0,
  total_trips: 0,
  notes: "",
  operator: null,
};

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const initials = (f = "", l = "") =>
  `${(f[0] || "").toUpperCase()}${(l[0] || "").toUpperCase()}` || "?";

const getStatusMeta = (status, label) => {
  return (
    STATUS_META[status] || {
      color: Colors.GRAY,
      bg: `${Colors.GRAY}26`,
      label: label || status || "—",
    }
  );
};

// 👇 ADDED: Helper to correctly resolve S3 URLs
const buildImageSource = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${FALLBACK_BASE_URL}/storage/${url.replace(/^\/+/, "")}`;
};

const formatDate = (v) => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return v;
  }
};

const resolveOperatorName = (d) => {
  if (d.operator) {
    return (
      d.operator.name ||
      `${d.operator.first_name || ""} ${d.operator.last_name || ""}`.trim()
    );
  }
  return d.operator_id || "-";
};

const displayName = (d) =>
  d.full_name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || "—";

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    /* ---- Page shell ---- */
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    /* ---- Header ---- */
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    /* ---- Stat cards ---- */
    .bk-stat { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px 18px; display: flex; align-items: center; gap: 12px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; }
    .bk-stat:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
    .bk-stat-val { font-size: 21px; font-weight: 700; line-height: 1; color: ${Colors.TEXT_DARK}; }
    .bk-stat-lbl { font-size: 11px; color: ${Colors.TEXT_LIGHT}; text-transform: uppercase; letter-spacing: .05em; margin-top: 4px; font-weight: 600; }

    /* ---- Card ---- */
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Toolbar ---- */
    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-search { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; height: auto !important; }
    .bk-search:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-search-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-select { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; color: ${Colors.TEXT_DARK}; height: auto !important; }
    .bk-select:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }

    /* ---- Table ---- */
    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; }
    .bk-table tbody tr:hover { background: ${Colors.LIGHT_GRAY}55; }

    /* ---- Avatars & chips ---- */
    .bk-avatar { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 13px; display: grid; place-items: center; flex-shrink: 0; }
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 3px 7px; border-radius: 5px; }

    /* ---- Pills ---- */
    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

    /* ---- Empty state ---- */
    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    /* ---- Skeleton ---- */
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* ---- Buttons ---- */
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }

    /* ---- Info sections ---- */
    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed ${Colors.BORDER_GRAY}; font-size: 13px; gap: 12px; }
    .bk-info-row:last-child { border-bottom: none; }
    .bk-info-label { color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .bk-info-value { color: ${Colors.TEXT_DARK}; font-weight: 600; text-align: right; max-width: 65%; word-break: break-word; }

    /* ---- Modal ---- */
    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    /* ---- Pagination ---- */
    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    /* ---- Tabs ---- */
    .nav-tabs { border-bottom: ${T.border}; gap: 4px; }
    .nav-tabs .nav-link { border: none !important; color: ${Colors.TEXT_LIGHT}; font-weight: 500; font-size: 13.5px; padding: 9px 15px; border-radius: ${T.radiusSm} ${T.radiusSm} 0 0; }
    .nav-tabs .nav-link:hover { color: ${Colors.TEXT_DARK}; background: ${Colors.LIGHT_GRAY}; }
    .nav-tabs .nav-link.active { color: ${Colors.PRIMARY} !important; background: #fff !important; border-bottom: 2px solid ${Colors.PRIMARY} !important; }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

/* ---------------- Status pill ---------------- */
const StatusPill = ({ status, label }) => {
  const m = getStatusMeta(status, label);
  return (
    <span className="bk-pill" style={{ color: m.color, background: m.bg }}>
      {label || m.label}
    </span>
  );
};

/* ---------------- Verified pill ---------------- */
const VerifiedPill = ({ verified, compact = false }) => {
  if (compact) {
    return verified ? (
      <span
        className="bk-pill"
        style={{ color: Colors.INFO, background: `${Colors.INFO}1f` }}
      >
        ✓
      </span>
    ) : null;
  }
  const color = verified ? Colors.INFO : Colors.GRAY;
  return (
    <span className="bk-pill" style={{ color, background: `${color}1f` }}>
      {verified ? "Yes" : "No"}
    </span>
  );
};

/* ---------------- Avatar / photo ---------------- */
const DriverAvatar = ({ driver, size = 34, radius = 10, fontSize }) => {
  const url = buildImageSource(driver.profile_image_url); // 👈 FIXED HERE
  if (url) {
    return (
      <Image
        src={url}
        alt={displayName(driver)}
        style={{
          width: size,
          height: size,
          objectFit: "cover",
          borderRadius: radius === "circle" ? "50%" : radius,
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <div
      className="bk-avatar"
      style={{
        width: size,
        height: size,
        borderRadius: radius === "circle" ? "50%" : radius,
        fontSize: fontSize || size * 0.38,
      }}
    >
      {initials(driver.first_name, driver.last_name)}
    </div>
  );
};

/* ---------------- Mobile card ---------------- */
const MobileDriverCard = ({ driver, onView }) => (
  <div className="bk-card mb-3">
    <div style={{ padding: 16 }}>
      <Row className="g-3">
        <Col xs={4}>
          <DriverAvatar driver={driver} size="100%" radius={T.radiusSm} />
        </Col>
        <Col xs={8}>
          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
            <div style={{ fontWeight: 700, color: Colors.TEXT_DARK }}>
              {displayName(driver)}
            </div>
            <StatusPill status={driver.status} label={driver.status_label} />
          </div>
          <div className="small text-muted mb-1">
            <strong>ID:</strong> {driver.driver_id}
          </div>
          <div className="small text-muted mb-1">
            <strong>License:</strong> {driver.license_number || "-"}
          </div>
          <div className="small text-muted mb-1">
            <strong>Phone:</strong> {driver.phone || "-"}
          </div>
          <div className="small text-muted mb-2">
            <strong>Operator:</strong> {resolveOperatorName(driver)}
          </div>
          {driver.is_verified && (
            <span
              className="bk-pill"
              style={{ color: Colors.INFO, background: `${Colors.INFO}1f` }}
            >
              Verified
            </span>
          )}
        </Col>
      </Row>
      <div style={{ borderTop: T.border, marginTop: 12, paddingTop: 12 }}>
        <Button
          className="bk-btn-ghost w-100"
          onClick={() => onView(driver.driver_id)}
        >
          <i className="bi bi-eye me-1" />
          View Details
        </Button>
      </div>
    </div>
  </div>
);

/* ---------------- Image preview tile ---------------- */
const ImageTile = ({ title, url, icon }) => {
  const finalUrl = buildImageSource(url); // 👈 FIXED HERE

  return (
    <div className="bk-section">
      <div className="bk-section-title">
        <i className={`bi ${icon}`} />
        {title}
      </div>
      <div className="text-center">
        {finalUrl ? (
          <Image
            src={finalUrl}
            alt={title}
            style={{
              maxWidth: "100%",
              maxHeight: 300,
              borderRadius: T.radiusSm,
            }}
          />
        ) : (
          <div className="text-muted py-4">
            <i className={`bi ${icon}`} style={{ fontSize: "2.5rem" }} />
            <p className="mt-2 mb-0" style={{ fontSize: 13 }}>
              No image
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const Drivers = () => {
  const nav = useNavigate();
  const abortControllerRef = useRef(null);

  /* ---------------- List state ---------------- */
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [operatorFilter, setOperatorFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [loading, setLoading] = useState(false);

  /* ---------------- Operators lookup ---------------- */
  const [operators, setOperators] = useState([]);

  /* ---------------- Detail modal ---------------- */
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  /* ---------------- Responsive ---------------- */
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  /* ================================================================ */
  /* DATA FETCHING                                                    */
  /* ================================================================ */

  const fetchRows = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        const params = {
          page,
          per_page: PER_PAGE,
          search: search || undefined,
          status: status === "All" ? undefined : status,
        };
        if (operatorFilter) params.operator_id = operatorFilter;
        if (verifiedFilter !== "") params.is_verified = verifiedFilter;

        const { data } = await api.get("/admin/drivers", { params, signal });

        if (data.success) {
          setRows(data.data || []);
          setStats(data.stats || {});
          setPages(data.pagination?.last_page || 1);
          setTotal(data.pagination?.total || 0);
        } else {
          throw new Error(data.message || "Failed to load drivers");
        }
      } catch (err) {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
        if (err.response?.status === 401) {
          localStorage.removeItem("arkila_admin_token");
          nav("/login");
        } else {
          MySwal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to load drivers.",
            confirmButtonColor: Colors.PRIMARY,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [page, search, status, operatorFilter, verifiedFilter, nav],
  );

  useEffect(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    fetchRows(abortControllerRef.current.signal);
    return () => abortControllerRef.current?.abort();
  }, [fetchRows]);

  /* Fetch operators lookup once */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/drivers/operators-list");
        if (data.success) setOperators(data.data || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  /* ================================================================ */
  /* ACTIONS                                                          */
  /* ================================================================ */

  const openModal = async (id) => {
    try {
      const { data } = await api.get(`/admin/drivers/${id}`);
      const d = data?.data;
      if (!d) throw new Error("Invalid response");

      setForm({
        ...EMPTY_FORM,
        driver_id: d.driver_id,
        full_name: d.full_name || "",
        first_name: d.first_name || "",
        last_name: d.last_name || "",
        middle_name: d.middle_name || "",
        suffix: d.suffix || "",
        email: d.email || "",
        phone: d.phone || "",
        license_number: d.license_number || "",
        license_expiry_date: d.license_expiry_date || "",
        license_type: d.license_type || "",
        address: d.address || "",
        city: d.city || "",
        province: d.province || "",
        postal_code: d.postal_code || "",
        date_of_birth: d.date_of_birth || "",
        gender: d.gender || "",
        emergency_contact_name: d.emergency_contact_name || "",
        emergency_contact_phone: d.emergency_contact_phone || "",
        hire_date: d.hire_date || "",
        base_salary: d.base_salary || "",
        employment_type: d.employment_type || "",
        status: d.status || "",
        status_label: d.status_label || "",
        status_color: d.status_color || "",
        is_verified: !!d.is_verified,
        verified_at: d.verified_at || "",
        profile_image_url: d.profile_image_url || null,
        license_image_front_url: d.license_image_front_url || null,
        license_image_back_url: d.license_image_back_url || null,
        average_rating: d.average_rating || 0,
        total_trips: d.total_trips || 0,
        notes: d.notes || "",
        operator: d.operator || null,
      });
      setShow(true);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        localStorage.removeItem("arkila_admin_token");
        nav("/login");
      } else {
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load driver details.",
          confirmButtonColor: Colors.PRIMARY,
        });
      }
    }
  };

  /* ================================================================ */
  /* PAGINATION                                                       */
  /* ================================================================ */

  const firstRow = (page - 1) * PER_PAGE + 1;
  const lastRow = Math.min(page * PER_PAGE, total);

  const renderPaginationItems = () => {
    const items = [];
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    const end = Math.min(pages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let n = start; n <= end; n++) {
      items.push(
        <Pagination.Item key={n} active={n === page} onClick={() => setPage(n)}>
          {n}
        </Pagination.Item>,
      );
    }
    return items;
  };

  /* ================================================================ */
  /* LOADING SKELETON                                                 */
  /* ================================================================ */

  if (loading && rows.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Drivers</h2>
            <p>Loading driver accounts…</p>
          </div>
          <Row className="g-3 mb-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Col key={i} xs={6} md={4} lg={2}>
                <div className="bk-stat">
                  <div
                    className="bk-skel"
                    style={{ width: 44, height: 44, borderRadius: 11 }}
                  />
                  <div className="flex-grow-1">
                    <div
                      className="bk-skel mb-2"
                      style={{ height: 18, width: "50%" }}
                    />
                    <div
                      className="bk-skel"
                      style={{ height: 11, width: "70%" }}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="bk-card" style={{ padding: 20 }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bk-skel mb-3" style={{ height: 42 }} />
            ))}
          </div>
        </Container>
      </div>
    );
  }

  /* ================================================================ */
  /* MAIN RENDER                                                      */
  /* ================================================================ */

  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        {/* ============ HEADER ============ */}
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Drivers</h2>
            <p>Browse all drivers across all operators (read-only)</p>
          </div>
        </div>

        {/* ============ STATS ============ */}
        {stats.total > 0 && (
          <Row className="g-3 mb-4">
            {STAT_CARDS.map((s) => (
              <Col key={s.k} xs={6} md={4} lg={2}>
                <div className="bk-stat">
                  <div
                    className="bk-stat-icon"
                    style={{ background: `${s.c}1f`, color: s.c }}
                  >
                    <i className={`bi ${s.i}`} />
                  </div>
                  <div>
                    <div className="bk-stat-val">{stats[s.field] ?? 0}</div>
                    <div className="bk-stat-lbl">{s.k}</div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}

        {/* ============ TOOLBAR ============ */}
        <div className="bk-toolbar">
          <Row className="g-2">
            <Col lg={4} md={6}>
              <InputGroup>
                <InputGroup.Text className="bk-search-icon">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <FormControl
                  className="bk-search"
                  placeholder="Search name / license / phone / email…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </InputGroup>
            </Col>
            <Col lg={2} md={3}>
              <Form.Select
                className="bk-select"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col lg={3} md={3}>
              <Form.Select
                className="bk-select"
                value={operatorFilter}
                onChange={(e) => {
                  setOperatorFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Operators</option>
                {operators.map((op) => (
                  <option key={op.operator_id} value={op.operator_id}>
                    {op.first_name} {op.last_name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col lg={3} md={12}>
              <Form.Select
                className="bk-select"
                value={verifiedFilter}
                onChange={(e) => {
                  setVerifiedFilter(e.target.value);
                  setPage(1);
                }}
              >
                {VERIFICATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* ============ TABLE ============ */}
        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i
                className="bi bi-person-badge"
                style={{ color: Colors.PRIMARY }}
              />
              Driver Accounts
            </h5>
            <small>
              Showing {firstRow}–{lastRow} of {total}
            </small>
          </div>

          {rows.length === 0 ? (
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-people" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>No drivers found</h5>
              <p className="text-muted mb-0">
                {search ||
                status !== "All" ||
                operatorFilter ||
                verifiedFilter !== ""
                  ? "No drivers match your filters."
                  : "No drivers registered yet."}
              </p>
            </div>
          ) : (
            <>
              {/* ---- Desktop: full table ---- */}
              <div className="d-none d-lg-block">
                <Table responsive hover className="bk-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Photo</th>
                      <th>Driver</th>
                      <th>Operator</th>
                      <th>License</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Verified</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.driver_id}>
                        <td>
                          <span className="bk-id-chip">#{d.driver_id}</span>
                        </td>
                        <td>
                          <DriverAvatar driver={d} size={40} radius="circle" />
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {displayName(d)}
                          </div>
                          <small className="text-muted">
                            {d.license_type || "-"}
                          </small>
                        </td>
                        <td>
                          <div
                            style={{
                              maxWidth: 140,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={resolveOperatorName(d)}
                          >
                            {resolveOperatorName(d)}
                          </div>
                        </td>
                        <td>{d.license_number || "-"}</td>
                        <td>{d.phone || "-"}</td>
                        <td>
                          <div
                            style={{
                              maxWidth: 130,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={d.email || "-"}
                          >
                            {d.email || "-"}
                          </div>
                        </td>
                        <td>
                          <StatusPill
                            status={d.status}
                            label={d.status_label}
                          />
                        </td>
                        <td>
                          <VerifiedPill verified={d.is_verified} />
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Button
                            className="bk-btn-ghost"
                            size="sm"
                            onClick={() => openModal(d.driver_id)}
                          >
                            <i className="bi bi-eye me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* ---- Tablet: condensed table ---- */}
              <div className="d-none d-md-block d-lg-none">
                <Table responsive hover className="bk-table">
                  <thead>
                    <tr>
                      <th>Driver</th>
                      <th>Details</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.driver_id}>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <DriverAvatar
                              driver={d}
                              size={50}
                              radius={14}
                              fontSize={16}
                            />
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {displayName(d)}
                              </div>
                              <small className="text-muted d-block">
                                ID: {d.driver_id}
                              </small>
                              <small className="text-muted">
                                {d.license_number || "-"}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            <div>
                              <strong>Operator:</strong>{" "}
                              {resolveOperatorName(d)}
                            </div>
                            <div>
                              <strong>Phone:</strong> {d.phone || "-"}
                            </div>
                            {d.email && (
                              <div>
                                <strong>Email:</strong> {d.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <StatusPill
                            status={d.status}
                            label={d.status_label}
                          />
                          {d.is_verified && (
                            <span className="ms-1">
                              <VerifiedPill verified={d.is_verified} compact />
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Button
                            className="bk-btn-ghost"
                            size="sm"
                            onClick={() => openModal(d.driver_id)}
                          >
                            <i className="bi bi-eye me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* ---- Mobile: cards ---- */}
              <div className="d-md-none p-3">
                {rows.map((d) => (
                  <MobileDriverCard
                    key={d.driver_id}
                    driver={d}
                    onView={openModal}
                  />
                ))}
              </div>

              {/* ---- Pagination ---- */}
              {pages > 1 && (
                <div
                  className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3"
                  style={{ padding: "14px 20px", borderTop: T.border }}
                >
                  <small className="text-muted">
                    Showing {firstRow} to {lastRow} of {total} entries
                  </small>
                  <Pagination
                    size={isMobile ? "sm" : undefined}
                    className="mb-0"
                  >
                    <Pagination.Prev
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={page === pages}
                      onClick={() => setPage(page + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>

        {/* ============ DETAIL MODAL ============ */}
        <Modal
          show={show}
          onHide={() => setShow(false)}
          size="lg"
          centered
          fullscreen={isMobile ? "sm-down" : false}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.PRIMARY}1f`,
                    color: Colors.PRIMARY,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-person-badge" />
                </span>
                Driver Details
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body
            style={{
              maxHeight: isMobile ? "calc(100vh - 120px)" : "70vh",
              overflowY: "auto",
            }}
          >
            <Tabs defaultActiveKey="profile" className="mb-3">
              {/* ---- Profile tab ---- */}
              <Tab eventKey="profile" title="Profile">
                {/* Identity */}
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-person" /> Identity
                  </div>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <DriverAvatar
                      driver={form}
                      size={56}
                      radius={16}
                      fontSize={18}
                    />
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          color: Colors.TEXT_DARK,
                        }}
                      >
                        {displayName(form)}
                      </div>
                      <small className="text-muted">
                        Driver #{form.driver_id}
                      </small>
                    </div>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Email</span>
                    <span className="bk-info-value">{form.email || "—"}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Phone</span>
                    <span className="bk-info-value">{form.phone || "—"}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Date of Birth</span>
                    <span className="bk-info-value">
                      {formatDate(form.date_of_birth)}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Gender</span>
                    <span className="bk-info-value">{form.gender || "—"}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Operator</span>
                    <span className="bk-info-value">
                      {form.operator
                        ? form.operator.name ||
                          `${form.operator.first_name || ""} ${
                            form.operator.last_name || ""
                          }`.trim()
                        : "—"}
                    </span>
                  </div>
                </div>

                {/* License */}
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-card-text" /> License
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Number</span>
                    <span className="bk-info-value">
                      {form.license_number || "—"}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Type</span>
                    <span className="bk-info-value">
                      {form.license_type || "—"}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Expiry</span>
                    <span className="bk-info-value">
                      {formatDate(form.license_expiry_date)}
                    </span>
                  </div>
                </div>

                {/* Address */}
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-geo-alt" /> Address
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Street</span>
                    <span className="bk-info-value">{form.address || "—"}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">City</span>
                    <span className="bk-info-value">{form.city || "—"}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Province</span>
                    <span className="bk-info-value">
                      {form.province || "—"}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Postal Code</span>
                    <span className="bk-info-value">
                      {form.postal_code || "—"}
                    </span>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-telephone" /> Emergency Contact
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Name</span>
                    <span className="bk-info-value">
                      {form.emergency_contact_name || "—"}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Phone</span>
                    <span className="bk-info-value">
                      {form.emergency_contact_phone || "—"}
                    </span>
                  </div>
                </div>

                {/* Employment */}
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-briefcase" /> Employment
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Type</span>
                    <span className="bk-info-value">
                      {form.employment_type || "—"}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Hire Date</span>
                    <span className="bk-info-value">
                      {formatDate(form.hire_date)}
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Base Salary</span>
                    <span className="bk-info-value">
                      {form.base_salary || "—"}
                    </span>
                  </div>
                </div>

                {/* Performance */}
                <div className="bk-section" style={{ marginBottom: 0 }}>
                  <div className="bk-section-title">
                    <i className="bi bi-star" /> Performance
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Rating</span>
                    <span className="bk-info-value">
                      {parseFloat(form.average_rating || 0).toFixed(2)} ★
                    </span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Total Trips</span>
                    <span className="bk-info-value">{form.total_trips}</span>
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Status</span>
                    <StatusPill
                      status={form.status}
                      label={form.status_label}
                    />
                  </div>
                  <div className="bk-info-row">
                    <span className="bk-info-label">Verified</span>
                    <span className="bk-info-value">
                      {form.is_verified ? "Yes" : "No"}
                    </span>
                  </div>
                  {form.verified_at && (
                    <div className="bk-info-row">
                      <span className="bk-info-label">Verified At</span>
                      <span className="bk-info-value">
                        {new Date(form.verified_at).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {form.notes && (
                    <div className="bk-info-row">
                      <span className="bk-info-label">Notes</span>
                      <span className="bk-info-value">{form.notes}</span>
                    </div>
                  )}
                </div>
              </Tab>

              {/* ---- Images tab ---- */}
              <Tab eventKey="images" title="Images">
                <Row className="g-3">
                  <Col md={4}>
                    <ImageTile
                      title="Profile Photo"
                      url={form.profile_image_url}
                      icon="bi-person"
                    />
                  </Col>
                  <Col md={4}>
                    <ImageTile
                      title="License (Front)"
                      url={form.license_image_front_url}
                      icon="bi-card-image"
                    />
                  </Col>
                  <Col md={4}>
                    <ImageTile
                      title="License (Back)"
                      url={form.license_image_back_url}
                      icon="bi-card-image"
                    />
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button className="bk-btn-ghost" onClick={() => setShow(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default Drivers;
